<template>
  <div
    v-if="hasSellingPlan"
    class="product__selling-plans"
  >
    <label
      v-if="!productForSellingRules.requires_selling_plan"
      class="product__otp product__subscribe-head flex items-center justify-between gap-3"
      :class="{ 'subscribe-selected': subscribe }"
      for="otp"
    >
      <input
        id="otp"
        v-model="otp"
        type="radio"
        :name="purchaseOptionRadioName"
      >
      <h3 class="product__subscribe-title">
        One Time Purchase
      </h3>

      <p class="product__subscribe-price ml-auto">
        <s v-if="oneTimeCompareAtPrice">{{ oneTimeCompareAtPrice }}</s>
        {{ originalPrice }}
      </p>
    </label>

    <div
      class="product__subscribe"
      :class="{ 'subscribe-selected': subscribe }"
    >
      <label
        class="product__subscribe-head flex items-center justify-between gap-3"
        for="subscribe"
      >
        <input
          id="subscribe"
          v-model="subscribe"
          type="radio"
          :name="purchaseOptionRadioName"
        >

        <h3 class="product__subscribe-title">
          {{ titles.subscribe }}
        </h3>

        <p class="product__subscribe-price ml-auto">
          <s>
            {{ originalPrice }}
          </s>
          <span>
            {{ selectedSellingPlan.price }}
          </span>
        </p>
      </label>

      <div
        v-if="subscriptionHtmlWithUsp"
        class="product__subscribe-text rte"
        v-html="subscriptionHtmlWithUsp"
      />

      <product-subscription-perks
        v-if="capturedPerksHtml"
        class="product__subscription-perks"
        :data-hide-on-one-time="capturedPerksHideOnOneTime"
        :data-ssr-visible="capturedPerksSsrVisible"
      >
        <div v-html="capturedPerksHtml" />
      </product-subscription-perks>

      <h4 class="product__subscribe-select-title">
        {{ titles.frequency }}
      </h4>
      <v-select
        v-model="selectedSellingPlan"
        class="product__subscribe-select"
        :options="sellingPlans"
        :clearable="false"
        :searchable="false"
        label="name"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useProductStore } from '../../pinia/ProductStore'
import { productPrice } from '../../helpers/product'

export default {
  props: {
    titles: {
      type: Object,
      default: null
    },
    isCombined: {
      type: Boolean,
      default: false
    },
    quickView: {
      type: Boolean,
      default: false
    },
    quickViewProduct: {
      type: Object,
      default: null
    },
    quickViewSelectedVariant: {
      type: Object,
      default: null
    },
    subscriptionText: {
      type: String,
      default: null
    },
    subscriptionUspsPayload: {
      type: [String, Array],
      default: () => []
    },
    subscriptionUspPackOptionPosition: {
      type: [Number, String],
      default: 0
    },
    defaultPurchaseType: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      subscribe: false,
      otp: true,
      selectedSellingPlan: {},
      initialDefaultApplied: false,
      capturedPerksHtml: '',
      capturedPerksSsrVisible: 'false',
      capturedPerksHideOnOneTime: 'false'
    }
  },
  computed: {
    ...mapState (useProductStore, {
      product: 'product',
      combinedListingsProductVariants: 'combinedListingsProductVariants',
      selectedVariant: 'selectedVariant',
      piniaSelectedSellingPlan: 'selectedSellingPlan'
    }),
    resolvedDefaultPurchaseType () {
      if (this.defaultPurchaseType) {
        return this.defaultPurchaseType
      }
      if (typeof window !== 'undefined' && window.theme && window.theme.defaultPurchaseType) {
        return window.theme.defaultPurchaseType
      }
      return 'subscription'
    },
    sellingPlanProduct () {
      if (this.isCombined) {
        return this.combinedListingsProductVariants.find(product =>
          product.variants.some(v => v.id === this.selectedVariant.id)
        )
      } else if (this.quickView) {
        return this.quickViewProduct.variants.find(variant => variant.id === this.quickViewSelectedVariant.id)
      }
      return this.product
    },
    hasSellingPlan () {
      return this.sellingPlanProduct?.selling_plan_groups?.length > 0
    },
    productForSellingRules () {
      return this.quickView ? this.quickViewProduct : this.product
    },
    originalPrice () {
      return productPrice(this.sellingPlanProduct.price)
    },
    oneTimeCompareAtPrice () {
      const { price, compare_at_price: compareAtPrice } = this.sellingPlanProduct
      return compareAtPrice > price ? productPrice(compareAtPrice) : null
    },
    price () {
      return '[price here]'
    },
    purchaseOptionRadioName () {
      const id = this.productForSellingRules?.id || this.product?.id || 'product'
      return `purchase-option-${id}`
    },
    sellingPlans () {
      if (!this.hasSellingPlan) return []
      const sellingPlans = this.sellingPlanProduct.selling_plan_groups[0].selling_plans.map((sellingPlan) => {
        let discount = 0

        if (sellingPlan.price_adjustments.length > 0 && sellingPlan.price_adjustments[0].value_type === 'percentage') {
          discount = (this.sellingPlanProduct.price / 100) * sellingPlan.price_adjustments[0].value
        }

        const price = productPrice(this.sellingPlanProduct.price - discount)

        return {
          ...sellingPlan,
          price,
          priceNoFormat: this.sellingPlanProduct.price - discount
        }
      })

      return sellingPlans
    },
    parsedSubscriptionUsps () {
      const raw = this.subscriptionUspsPayload
      if (Array.isArray(raw)) return raw
      if (!raw || typeof raw !== 'string') return []
      try {
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed : []
      } catch {
        return []
      }
    },
    subscriptionHtmlWithUsp () {
      const base = this.subscriptionText || ''
      if (!base) return ''
      const rows = this.parsedSubscriptionUsps
      const pos = Number(this.subscriptionUspPackOptionPosition) || 0
      if (!rows.length || !pos) return base

      const variant = this.resolveVariantForPackMatch()
      const packLabel = this.getPackOptionLabel(variant, pos)
      const usp = this.matchSubscriptionUspForPack(rows, packLabel)
      if (!usp) return base

      return this.injectSubscriptionLi(base, usp)
    }
  },
  watch: {
    subscribe (subscribe) {
      if (subscribe) {
        this.changeSellingPlan(this.selectedSellingPlan)
      } else {
        this.changeSellingPlan(null)
      }
      this.otp = !subscribe
    },
    otp (otp) {
      if (otp) {
        this.changeSellingPlan(null)
      }
      this.subscribe = !otp
    },
    selectedSellingPlan () {
      if (this.piniaSelectedSellingPlan) {
        this.changeSellingPlan(this.selectedSellingPlan)
      }
    },
    sellingPlanProduct () {
      if (!this.hasSellingPlan) {
        this.selectedSellingPlan = {}
        this.changeSellingPlan(null)
        return
      }
      this.selectedSellingPlan = this.sellingPlans[0]
      if (this.piniaSelectedSellingPlan) {
        this.changeSellingPlan(this.selectedSellingPlan)
      }
    },
    hasSellingPlan (has) {
      if (!has) {
        this.changeSellingPlan(null)
        return
      }
      if (!this.initialDefaultApplied) {
        this.$nextTick(() => this.applyDefaultPurchaseSelection())
      }
    }
  },
  beforeMount () {
    const captures = typeof window !== 'undefined' ? window.__subscriptionPerksCaptures : null
    if (!captures?.length) return

    const captured = captures.shift()
    if (!captured) return

    this.capturedPerksHtml = captured.html
    this.capturedPerksSsrVisible = captured.ssrVisible
    this.capturedPerksHideOnOneTime = captured.hideOnOneTime || 'false'
  },
  created () {
    if (this.hasSellingPlan) {
      this.applyDefaultPurchaseSelection()
    }
  },
  mounted () {
    if (!this.initialDefaultApplied) {
      this.applyDefaultPurchaseSelection()
    }
  },
  methods: {
    ...mapActions(useProductStore, [
      'changeSellingPlan'
    ]),
    applyDefaultPurchaseSelection () {
      if (!this.hasSellingPlan) {
        this.changeSellingPlan(null)
        return
      }
      this.initialDefaultApplied = true
      this.selectedSellingPlan = this.sellingPlans[0] || {}
      const subOnly = this.productForSellingRules.requires_selling_plan
      const wantSub = subOnly || this.resolvedDefaultPurchaseType === 'subscription'
      this.subscribe = wantSub
      this.otp = !wantSub
      if (wantSub) {
        this.changeSellingPlan(this.selectedSellingPlan)
      } else {
        this.changeSellingPlan(null)
      }
    },

    resolveVariantForPackMatch () {
      if (this.quickView) {
        return this.quickViewSelectedVariant
      }

      if (this.isCombined && this.sellingPlanProduct?.variants?.length) {
        const childVariant = this.sellingPlanProduct.variants.find(
          variant => variant.id === this.selectedVariant?.id
        )
        if (childVariant) return childVariant
      }

      return this.selectedVariant
    },

    getPackOptionLabel (variant, position) {
      if (!variant) return ''
      if (position === 1) return String(variant.option1 || '').trim()
      if (position === 2) return String(variant.option2 || '').trim()
      if (position === 3) return String(variant.option3 || '').trim()
      return ''
    },

    matchSubscriptionUspForPack (rows, ovName) {
      const ov = String(ovName || '').trim()
      if (!ov) return ''
      const ovLower = ov.toLowerCase()
      for (const row of rows) {
        const moKey = String(row.variantName || '').trim()
        if (moKey.toLowerCase() === ovLower) return String(row.subscriptionUsp || '')
        const ovHead = ov.replace(/-/g, ' ').split(/\s+/).filter(Boolean)[0] || ''
        if (moKey && ovHead && moKey.toLowerCase() === ovHead.toLowerCase()) return String(row.subscriptionUsp || '')
      }
      return ''
    },

    escapeHtml (text) {
      if (typeof document === 'undefined') {
        return String(text)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
      }
      const div = document.createElement('div')
      div.textContent = text
      return div.innerHTML
    },

    injectSubscriptionLi (html, usp) {
      const escaped = this.escapeHtml(String(usp))
      const li = `<li>${escaped}</li>`
      if (html.includes('<ul>')) {
        return html.replace('<ul>', `<ul>${li}`)
      }
      return `<ul>${li}</ul>${html}`
    }
  }
}
</script>
